import React, { useState, useRef } from "react";
import {
	Box,
	Typography,
	TextField,
	IconButton,
	CircularProgress,
	Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PlaceIcon from "@mui/icons-material/Place";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { BasicModal } from "../BasicModal/BasicModal";
import imageCompression from "browser-image-compression";
import { useSnackbar } from "notistack";
import travelKitPortada from "../../assets/travel-kit-portada.png";
import {
	TRAVEL_KIT_MODAL,
	textFieldSx,
	modalContentSx,
	primaryButtonSx,
	secondaryButtonSx,
} from "./TravelKitModalStyles";

// Template101: Travel Kit - Portada del Viaje
const Template101 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	registerScrollContainer,
	isViewer = false,
}) => {
	const setScrollContainerRef = (domNode) => {
		if (typeof registerScrollContainer === "function") {
			registerScrollContainer(domNode);
		}
	};
	const { enqueueSnackbar } = useSnackbar();
	const [loadingImage, setLoadingImage] = useState(false);
	const [editModal, setEditModal] = useState(false);
	const inputRef = useRef(null);
	const processingRef = useRef(false);
	const cancelHandlerRef = useRef(null);

	// Obtener datos del Travel Kit desde page.data (defensivo: servidor puede devolver otra estructura)
	const pageData = page?.data ?? {};
	// En el Viewer/API la página viene como { id, order, data }; las imágenes están en page.data.images
	const images = Array.isArray(page?.images) ? page.images : (Array.isArray(page?.data?.images) ? page.data.images : []);
	const backgroundImageUrl = images[0]?.src || images[0]?.url;
	const [tripData, setTripData] = useState(() => ({
		tripName: pageData.tripName || "Mi Viaje",
		destination: pageData.destination || "Destino",
		startDate: pageData.startDate || "",
		endDate: pageData.endDate || "",
		travelers: pageData.travelers || "2 viajeros",
	}));

	const [tempData, setTempData] = useState({ ...tripData });

	// Función para actualizar page.data
	const updatePageData = (newData) => {
		updatePage({
			...page,
			data: {
				...pageData,
				...newData,
			},
		});
	};

	const compressImage = async (file) => {
		const options = {
			maxSizeMB: 0.75,
			maxWidthOrHeight: 1280,
			useWebWorker: true,
			maxIterations: 100,
		};
		try {
			return await imageCompression(file, options);
		} catch (error) {
			enqueueSnackbar("La imagen no es compatible. Inténtalo con otra.", {
				variant: "error",
			});
			return null;
		}
	};

	const handleImageChange = async (event) => {
		processingRef.current = true;
		if (cancelHandlerRef.current) {
			window.removeEventListener("focus", cancelHandlerRef.current);
			cancelHandlerRef.current = null;
		}

		let rawFile;
		if (event.dataTransfer?.files) {
			rawFile = event.dataTransfer.files[0];
		} else if (event.target?.files) {
			rawFile = event.target.files[0];
		}

		if (!rawFile) {
			processingRef.current = false;
			setLoadingImage(false);
			return;
		}

		const file = await compressImage(rawFile);
		if (file) {
			const reader = new FileReader();
			reader.onload = () => {
				let auxPage = { ...page };
				const prevImages = Array.isArray(auxPage.images) ? [...auxPage.images] : [];
				prevImages[0] = {
					id: -new Date().getTime(),
					src: reader.result,
				};
				auxPage.images = prevImages;
				updatePage({ ...auxPage });
				processingRef.current = false;
				setLoadingImage(false);
			};
			reader.onerror = () => {
				processingRef.current = false;
				setLoadingImage(false);
			};
			reader.readAsDataURL(file);
		} else {
			processingRef.current = false;
			setLoadingImage(false);
		}
	};

	const handleClickImageInput = () => {
		const inputElement = inputRef.current;
		if (inputElement) {
			if (cancelHandlerRef.current) {
				window.removeEventListener("focus", cancelHandlerRef.current);
			}
			setLoadingImage(true);
			cancelHandlerRef.current = () => {
				setTimeout(() => {
					if (processingRef.current) {
						if (cancelHandlerRef.current) {
							window.removeEventListener("focus", cancelHandlerRef.current);
							cancelHandlerRef.current = null;
						}
						return;
					}
					const hasFile = inputElement.files?.length > 0;
					if (!hasFile) {
						setLoadingImage(false);
					}
					if (cancelHandlerRef.current) {
						window.removeEventListener("focus", cancelHandlerRef.current);
						cancelHandlerRef.current = null;
					}
				}, 300);
			};
			window.addEventListener("focus", cancelHandlerRef.current, {
				once: true,
			});
			inputElement.click();
		}
	};

	const handleOpenEdit = () => {
		if (isViewer) return;
		setTempData({ ...tripData });
		setEditModal(true);
	};

	const handleSaveEdit = () => {
		setTripData(tempData);
		updatePageData(tempData);
		setEditModal(false);
	};

	const formatDateRange = () => {
		if (!tripData.startDate && !tripData.endDate) return "Fechas por definir";
		const options = { day: "numeric", month: "short" };
		const start = tripData.startDate
			? new Date(tripData.startDate).toLocaleDateString("es-ES", options)
			: "?";
		const end = tripData.endDate
			? new Date(tripData.endDate).toLocaleDateString("es-ES", options)
			: "?";
		return `${start} - ${end}`;
	};

	const templateItemProps = {
		"data-template-item": true,
		className: "template-item",
	};

	// Estilo scrapbook/kawaii: paleta pastel y stickers
	const scrapbookBg = backgroundColor || "#FFF8F5";
	const stickerBg = "rgba(255, 255, 255, 0.92)";
	const cardShadow =
		"0 4px 20px rgba(232, 180, 188, 0.2), 0 1px 3px rgba(0,0,0,0.06)";

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				width: "100%",
				backgroundColor: scrapbookBg,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				overflow: "hidden",
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* Input oculto + label permiten que el clic sea gesto directo (evita bloqueo del navegador) */}
			<input
				id={`template101-cover-input-${page.id ?? "default"}`}
				type="file"
				accept="image/*"
				onChange={handleImageChange}
				style={{ display: "none" }}
				ref={inputRef}
				className="hide-in-viewer"
			/>

			<Box
				sx={{
					position: "absolute",
					inset: 0,
					zIndex: 0,
				}}
			>
				{(backgroundImageUrl || !loadingImage) && (
					<Box
						onDrop={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setLoadingImage(true);
							handleImageChange(e);
						}}
						onDragOver={(e) => e.preventDefault()}
						sx={{
							width: "100%",
							height: "100%",
							backgroundImage: `url(${backgroundImageUrl || travelKitPortada})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
							"&::after": {
								content: '""',
								position: "absolute",
								inset: 0,
								background:
									"linear-gradient(180deg, rgba(255,248,245,0.2) 0%, rgba(232,180,188,0.4) 100%)",
							},
						}}
					/>
				)}
				{loadingImage && (
					<Box
						sx={{
							position: "absolute",
							inset: 0,
							backgroundColor: "rgba(255, 248, 245, 0.95)",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress sx={{ color: "#E8B4BC" }} />
					</Box>
				)}
			</Box>

			{/* Content Overlay */}
			<Box
				sx={{
					position: "relative",
					zIndex: 1,
					display: "flex",
					flexDirection: "column",
					height: "100%",
					padding: "24px",
				}}
			>
				{/* Header: badge tipo sticker kawaii + iconos */}
				<Box
					data-block-id="header"
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							backgroundColor: "rgb(248, 250, 252)",
							color: "#5C3A45",
							padding: "10px 18px",
							borderRadius: "24px",
							boxShadow:
								"0 3px 12px rgba(232, 180, 188, 0.35), 0 1px 2px rgba(0,0,0,0.08)",
							border: "2px solid rgba(255,255,255,0.8)",
							transform: "rotate(-2deg)",
						}}
					>
						<FlightTakeoffIcon sx={{ fontSize: 20, opacity: 0.9 }} />
						<Typography
							sx={{
								color: "inherit",
								fontSize: "13px !important",
								fontWeight: "700 !important",
								letterSpacing: "0.5px",
							}}
						>
							✿ TRAVEL KIT ✿
						</Typography>
					</Box>

					<Box className="hide-in-viewer" sx={{ display: "flex", gap: 0.5 }}>
						<Box
							component="label"
							htmlFor={`template101-cover-input-${page.id ?? "default"}`}
							sx={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: "rgba(255,255,255,0.9)",
								color: "#5C3A45",
								boxShadow: "0 2px 10px rgba(232, 180, 188, 0.25)",
								border: "2px solid rgba(255,255,255,0.8)",
								borderRadius: "50%",
								width: 40,
								height: 40,
								cursor: "pointer",
								"&:hover": { backgroundColor: "#FFE4EC" },
							}}
							title="Cambiar imagen de portada"
							{...templateItemProps}
						>
							<PhotoCameraIcon
								sx={{ fontSize: 20, pointerEvents: "none" }}
							/>
						</Box>
						<IconButton
							onClick={(e) => {
								e.stopPropagation();
								if (isViewer) return;
								handleOpenEdit();
							}}
							sx={{
								backgroundColor: "rgba(255,255,255,0.9)",
								color: "#5C3A45",
								boxShadow: "0 2px 10px rgba(232, 180, 188, 0.25)",
								border: "2px solid rgba(255,255,255,0.8)",
								"&:hover": { backgroundColor: "#FFE4EC" },
							}}
							title="Editar información del viaje"
							{...templateItemProps}
						>
							<EditIcon
								sx={{ fontSize: 20, pointerEvents: "none" }}
							/>
						</IconButton>
					</Box>
				</Box>

				{/* Main Content - Trip Name tipo título scrapbook (clic para editar) */}
				<Box
					data-block-id="text-1"
					{...templateItemProps}
					onClick={(e) => {
						if (isViewer) return;
						e.stopPropagation();
						handleOpenEdit();
					}}
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						textAlign: "center",
						gap: 2,
						cursor: isViewer ? "default" : "pointer",
					}}
				>
					<Typography
						sx={{
							color: "#fff",
							fontSize: "34px !important",
							fontWeight: "800 !important",
							lineHeight: "1.2 !important",
							textShadow: "0 2px 16px rgba(0,0,0,0.25)",
							maxWidth: "90%",
						}}
					>
						{tripData.tripName}
					</Typography>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							backgroundColor: "rgba(255,255,255,0.9)",
							color: "#5C3A45",
							padding: "12px 22px",
							borderRadius: "24px",
							boxShadow: "0 3px 14px rgba(232, 180, 188, 0.3)",
							border: "2px dashed rgba(232, 180, 188, 0.6)",
							transform: "rotate(1deg)",
						}}
					>
						<PlaceIcon sx={{ fontSize: 20, opacity: 0.9 }} />
						<Typography
							sx={{
								color: "inherit",
								fontSize: "16px !important",
								fontWeight: "700 !important",
							}}
						>
							{tripData.destination}
						</Typography>
					</Box>
				</Box>

				{/* Bottom: cards tipo recorte scrapbook / washi (clic para editar) */}
				<Box
					data-block-id="text-2"
					sx={{
						display: "flex",
						gap: 2,
						justifyContent: "center",
						flexWrap: "wrap",
					}}
				>
					<Box
						{...templateItemProps}
						onClick={(e) => {
							e.stopPropagation();
							handleOpenEdit();
						}}
						sx={{
							backgroundColor: stickerBg,
							borderRadius: "20px",
							padding: "14px 20px",
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							boxShadow: cardShadow,
							border: "2px solid rgba(232, 180, 188, 0.4)",
							transform: "rotate(-1deg)",
							cursor: "pointer",
						}}
					>
						<CalendarTodayIcon sx={{ color: "#E8B4BC", fontSize: 24 }} />
						<Box>
							<Typography
								sx={{
									fontSize: "10px !important",
									color: "#B8959E",
									fontWeight: "600 !important",
									letterSpacing: "0.5px",
								}}
							>
								FECHAS
							</Typography>
							<Typography
								sx={{
									fontSize: "14px !important",
									color: "#5C3A45",
									fontWeight: "700 !important",
								}}
							>
								{formatDateRange()}
							</Typography>
						</Box>
					</Box>

					<Box
						{...templateItemProps}
						onClick={(e) => {
							e.stopPropagation();
							handleOpenEdit();
						}}
						sx={{
							backgroundColor: stickerBg,
							borderRadius: "20px",
							padding: "14px 20px",
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							boxShadow: cardShadow,
							border: "2px solid rgba(232, 180, 188, 0.4)",
							transform: "rotate(1.5deg)",
							cursor: "pointer",
						}}
					>
						<Box
							sx={{
								width: 28,
								height: 28,
								borderRadius: "50%",
								backgroundColor: "#E8B4BC",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: "13px",
								color: "#fff",
								fontWeight: "bold",
							}}
						>
							{tripData.travelers?.charAt(0) || "2"}
						</Box>
						<Box>
							<Typography
								sx={{
									fontSize: "10px !important",
									color: "#B8959E",
									fontWeight: "600 !important",
									letterSpacing: "0.5px",
								}}
							>
								VIAJEROS
							</Typography>
							<Typography
								sx={{
									fontSize: "14px !important",
									color: "#5C3A45",
									fontWeight: "700 !important",
								}}
							>
								{tripData.travelers}
							</Typography>
						</Box>
					</Box>
				</Box>
			</Box>

			{/* Edit Modal - diseño coherente con dashboard (oculto en viewer) */}
			<Box className="hide-in-viewer">
			<BasicModal
				width={420}
				showModal={editModal}
				handleClose={() => setEditModal(false)}
				contentSx={modalContentSx}
			>
				<Box sx={{ p: 3 }}>
					<Typography
						sx={{
							fontSize: "18px !important",
							fontWeight: 700,
							color: TRAVEL_KIT_MODAL.textPrimary,
							mb: 2.5,
							fontFamily: "'Inter', sans-serif",
						}}
					>
						Editar información del viaje
					</Typography>

					<TextField
						label="Nombre del viaje"
						value={tempData.tripName}
						onChange={(e) =>
							setTempData({ ...tempData, tripName: e.target.value })
						}
						fullWidth
						margin="normal"
						size="small"
						placeholder="Ej: Aventura en Japón"
						sx={textFieldSx}
					/>

					<TextField
						label="Destino"
						value={tempData.destination}
						onChange={(e) =>
							setTempData({ ...tempData, destination: e.target.value })
						}
						fullWidth
						margin="normal"
						size="small"
						placeholder="Ej: Tokio, Japón"
						sx={textFieldSx}
					/>

					<Box sx={{ display: "flex", gap: 2, mt: 1 }}>
						<TextField
							label="Fecha inicio"
							type="date"
							value={tempData.startDate}
							onChange={(e) =>
								setTempData({ ...tempData, startDate: e.target.value })
							}
							fullWidth
							margin="normal"
							size="small"
							InputLabelProps={{ shrink: true }}
							sx={textFieldSx}
						/>
						<TextField
							label="Fecha fin"
							type="date"
							value={tempData.endDate}
							onChange={(e) =>
								setTempData({ ...tempData, endDate: e.target.value })
							}
							fullWidth
							margin="normal"
							size="small"
							InputLabelProps={{ shrink: true }}
							sx={textFieldSx}
						/>
					</Box>

					<TextField
						label="Viajeros"
						value={tempData.travelers}
						onChange={(e) =>
							setTempData({ ...tempData, travelers: e.target.value })
						}
						fullWidth
						margin="normal"
						size="small"
						placeholder="Ej: 2 adultos, 1 niño"
						sx={textFieldSx}
					/>

					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1.5,
							mt: 3,
						}}
					>
						<Button
							onClick={() => setEditModal(false)}
							sx={secondaryButtonSx}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveEdit}
							sx={primaryButtonSx}
						>
							Guardar
						</Button>
					</Box>
				</Box>
			</BasicModal>
			</Box>
		</Box>
	);
};

Template101.thumbnail = "/images/templates/template-tk-portada.png";

export default Template101;
